import isEmpty from 'lodash/isEmpty';
import flatMap from 'lodash/flatMap';
import forEach from 'lodash/forEach';
import map from 'lodash/map';
import { transformAndValidateSync } from 'class-transformer-validator';

import { DomainError as StandardDomainError } from '@ad-scripts/application/lib/error';
import { Code, DomainError } from 'src/models/DomainError';

/**
 * Validate object against the constraints of a Typescript type. If validation fails, throw
 * StandardDomainError error, otherwise return true
 * @param Type Typescript type to validate object against
 * @param obj plain javascript object that should represent type
 */
function validateType(Type: any, obj: any) {
  try {
    transformAndValidateSync(Type, obj);
  } catch (e) {
    const errors = mapToDomainError(e);
    throw new StandardDomainError(errors);
  }
  return true;
}
export default validateType;

// map of class-validator constraint codes to StandardDomainError codes
const errorMap: { [key: string]: Code } = {
  invalid: 'invalid',
  isNotEmpty: 'blank',
  isEmpty: 'present',
  isNumber: 'not_a_number',
  min: 'greater_than',
  max: 'less_than_or_equal_to',
  maxLength: 'too_long',
  length: 'wrong_length',
};

function getValidationError(validationError, parentProperty = '') {
  const { property, children, constraints } = validationError;

  // Checks for a number property which is actually an array index for checking inside nested arrays
  const childProp = isNaN(property) ? `.${property}` : `[${property}]`;
  const attr = parentProperty ? `${parentProperty}${childProp}` : property;

  const childrenErrors = isEmpty(children)
    ? []
    : flatMap(validationError.children, childError => getValidationError(childError, attr));

  const ownErrors = map(constraints, (message, constraint) => {
    return {
      attr,
      code: errorMap[constraint] || constraint,
      detail: message,
    };
  });

  return [...childrenErrors, ...ownErrors];
}

export function mapToDomainError(validationErrors) {
  return flatMap(validationErrors, validationError => getValidationError(validationError));
}

interface Err {
  toJs: Function;
}
export function translateErrors(errors: Err[], getErrorMessage?: (error: DomainError) => string): DomainError[] {
  let transformedErrors = map(errors, err => err.toJs());
  if (getErrorMessage) {
    transformedErrors = forEach(transformedErrors, err => {
      const message = getErrorMessage(err);
      if (message) {
        err.detail = message;
      }
    });
  }
  return transformedErrors;
}
